<template>
    <nav
        v-show="!keyboardHeight"
        ref="navEl"
        class="mobile-nav md:hidden fixed bottom-0 left-0 right-0 z-20 flex w-full justify-around items-stretch bg-nav-bg border-t border-border px-2 pt-2">
        <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            :data-testid="`mobile-nav-${tab.id}`"
            :aria-label="tabAriaLabel(tab)"
            :aria-current="isActive(tab) ? 'page' : undefined"
            class="relative flex flex-col items-center justify-center gap-1 min-w-14 transition-colors"
            :class="isActive(tab) ? 'text-nav-tab-active-text' : 'text-nav-icon'"
            @click="handleTabClick(tab)">
            <span
                aria-hidden="true"
                class="absolute inset-x-0 -top-2 h-[3px] rounded-b-sm transition-colors"
                :class="isActive(tab) && tab.id !== MainMenuPane.NEW_CHAT ? 'bg-nav-tab-active-indicator' : 'bg-transparent'" />
            <template v-if="tab.id === MainMenuPane.NEW_CHAT">
                <div class="flex items-center justify-center w-10 h-10 rounded-full bg-accent shadow-lg">
                    <i class="bi bi-plus-lg text-white text-lg" aria-hidden="true" />
                </div>
            </template>
            <template v-else>
                <span class="relative inline-flex">
                    <i class="bi text-xl/none" :class="tab.icon" aria-hidden="true" />
                    <BaseCountBadge v-if="tabCount(tab) > 0" :count="tabCount(tab)" class="absolute -top-1 -right-2" aria-hidden="true" />
                </span>
                <span class="details">{{ tab.label }}</span>
            </template>
        </button>
    </nav>

    <MobileMoreSheet :open="moreSheetOpen" @close="moreSheetOpen = false" @open-privacy="privacyDialogOpen = true" />
    <PrivacyNoticeDialog v-model:open="privacyDialogOpen" />
</template>

<script setup lang="ts">
import { router, usePage } from "@inertiajs/vue3";
import BaseCountBadge from "~vue/components/BaseCountBadge.vue";
import { useKeyboard } from "~vue/composables/useKeyboard";
import PrivacyNoticeDialog from "~vue/Onboarding/PrivacyNoticeDialog.vue";
import { isInIFrame } from "~vue/utils";
import { computed, onBeforeUnmount, onMounted, provide, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import { MainMenuPane, paneNotificationAriaLabel, type PaneNotificationCounts, validateDisabledPanes } from "./menuPanes";
import MobileMoreSheet from "./MobileMoreSheet.vue";

const { t } = useI18n();

interface Tab {
    id: MainMenuPane | "more";
    label: string;
    icon: string;
    url?: string;
}

type TabDefinition = Omit<Tab, "label"> & { label: () => string };

const props = withDefaults(
    defineProps<{
        paneNotificationCounts?: PaneNotificationCounts;
    }>(),
    {
        paneNotificationCounts: () => ({}),
    },
);

const page = usePage();

// Canonical five-tab layout keeps the ➕ New Chat button centered on mobile.
// The fourth slot prefers MY_WORK (gated by the NADIA_LABS_PAGE feature flag);
// when it is unavailable we promote PROFILE in its place and hide the duplicate
// Profile row in the More sheet.
const HOME_TAB: TabDefinition = { id: MainMenuPane.HOME, label: () => t("navigation.panes.home"), icon: "bi-house", url: "/" };
const HISTORY_TAB: TabDefinition = { id: MainMenuPane.HISTORY, label: () => t("navigation.mobileTabs.history"), icon: "bi-chat", url: "/past-conversations" };
const NEW_CHAT_TAB: TabDefinition = { id: MainMenuPane.NEW_CHAT, label: () => t("navigation.legacyPanes.newChat"), icon: "bi-plus-lg", url: "/coach/start/" };
const MY_WORK_TAB: TabDefinition = { id: MainMenuPane.MY_WORK, label: () => t("navigation.legacyPanes.myWork"), icon: "bi-journal-bookmark", url: "/intelligence/my-work/" };
const PROFILE_TAB: TabDefinition = { id: MainMenuPane.PROFILE, label: () => t("navigation.panes.profile"), icon: "bi-person-circle", url: "/accounts/user-profile" };
const MORE_TAB: TabDefinition = { id: "more", label: () => t("navigation.mobileTabs.more"), icon: "bi-three-dots" };

// URLs reachable from MobileMoreSheet — used to light up the "More" tab when the
// user is on one of those pages. Keep in sync with the items list in
// MobileMoreSheet.vue.
const MORE_SECTION_URLS = [
    "/onboarding/calendar-first/setup/",
    "/accounts/user-profile",
    "/accounts/collaboration-profile",
    "/accounts/user-preferences",
    "/journeys/",
    "/development-journeys/",
    "/people/",
    "/perspective/teams/",
    "/performance_management",
    "/reflections",
    "/client-analytics/",
];

const moreSheetOpen = ref(false);
const privacyDialogOpen = ref(false);
// Plain (non-reactive) lock — using a ref would queue a re-render on every
// click that races with Inertia's page swap and produces null-vnode errors.
let navigating = false;

const disabledPanes = ref([
    MainMenuPane.GUIDED_SETUP,
    MainMenuPane.COLLABORATION_PROFILE,
    MainMenuPane.CONNECTIONS,
    MainMenuPane.JOURNEYS,
    MainMenuPane.DEVELOPMENT_JOURNEYS,
    MainMenuPane.MY_WORK,
    MainMenuPane.PERFORMANCE_MANAGEMENT,
    MainMenuPane.REFLECTIONS,
]);

const isMyWorkAvailable = computed(() => !disabledPanes.value.includes(MainMenuPane.MY_WORK));

// Mirror of `disabledPanes` with PROFILE appended when we promote it to the tab
// bar, so MobileMoreSheet can suppress the duplicate row.
const moreSheetDisabledPanes = computed(() => (isMyWorkAvailable.value ? disabledPanes.value : [...disabledPanes.value, MainMenuPane.PROFILE]));
provide("mobileDisabledPanes", moreSheetDisabledPanes);

const tabs = computed<Tab[]>(() =>
    [HOME_TAB, HISTORY_TAB, NEW_CHAT_TAB, isMyWorkAvailable.value ? MY_WORK_TAB : PROFILE_TAB, MORE_TAB].map((tab) => ({ ...tab, label: tab.label() })),
);

const tabCount = (tab: Tab): number => (tab.id === "more" ? 0 : (props.paneNotificationCounts[tab.id] ?? 0));

const tabAriaLabel = (tab: Tab): string => (tab.id === "more" ? tab.label : paneNotificationAriaLabel(tab.id, tab.label, tabCount(tab)));

const urlMatchesTab = (tab: Tab): boolean => {
    if (tab.id === MainMenuPane.HOME) {
        return page.url === "/" || page.url.startsWith("/?");
    }
    return tab.url ? page.url.startsWith(tab.url) : false;
};

const isActive = (tab: Tab): boolean => {
    if (tab.id === "more") {
        if (moreSheetOpen.value) {
            return true;
        }
        // Defer to any other tab whose URL matches (e.g. Profile when promoted
        // into the bottom row). Otherwise highlight More for routes it owns.
        const matchesAnotherTab = tabs.value.some((t) => t.id !== "more" && urlMatchesTab(t));
        if (matchesAnotherTab) {
            return false;
        }
        return MORE_SECTION_URLS.some((url) => page.url.startsWith(url));
    }
    return urlMatchesTab(tab);
};

const handleTabClick = (tab: Tab) => {
    if (tab.id === "more") {
        moreSheetOpen.value = !moreSheetOpen.value;
        return;
    }
    if (!tab.url || navigating) {
        return;
    }
    // Tapping the already-active tab scrolls the page back to the top (standard
    // mobile pattern) without triggering a redundant Inertia page swap whose
    // remount cycle has racy edges with the transition.
    if (urlMatchesTab(tab)) {
        moreSheetOpen.value = false;
        const behavior = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
        navEl.value?.closest("[scroll-region]")?.scrollTo({ top: 0, behavior });
        return;
    }
    moreSheetOpen.value = false;
    if (isInIFrame()) {
        globalThis.location.href = tab.url.includes("client=teams") ? tab.url : `${tab.url}?client=teams`;
        return;
    }
    navigating = true;
    router.visit(tab.url, {
        preserveScroll: false,
        onFinish: () => {
            navigating = false;
        },
    });
};

// Keep --mobile-nav-total-height in sync with the bar's real rendered height so
// the layout reserves exactly enough bottom padding (the static 4rem fallback in
// the stylesheet underestimates the bar once labels + safe-area insets are added,
// which let page content slip behind the fixed nav). offsetHeight already
// includes the safe-area padding-bottom.
const navEl = useTemplateRef<HTMLElement>("navEl");
let resizeObserver: ResizeObserver | undefined;

const syncNavHeight = () => {
    const height = navEl.value?.offsetHeight ?? 0;
    // Skip 0 — the bar is display:none while the keyboard is open (and on md+
    // screens). Keep the last good value; the layout zeroes its own reservation
    // in those states.
    if (height > 0) {
        document.documentElement.style.setProperty("--mobile-nav-total-height", `${height}px`);
    }
};

onMounted(async () => {
    syncNavHeight();
    if (navEl.value && "ResizeObserver" in globalThis) {
        resizeObserver = new ResizeObserver(syncNavHeight);
        resizeObserver.observe(navEl.value);
    }
    disabledPanes.value = await validateDisabledPanes(disabledPanes.value);
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
});

// Hide the nav while the soft keyboard is open — matches the platform
// convention (Instagram, WhatsApp, etc.) and avoids competing with the
// composer for screen real estate. `KeyboardResize.Native` in
// capacitor.config.ts resizes the activity window so the composer naturally
// sits above the keyboard; we just need to take the nav out of the way.
const { keyboardHeight } = useKeyboard();
</script>

<style>
:root {
    --mobile-nav-total-height: calc(4rem + env(safe-area-inset-bottom));
}

.mobile-nav {
    padding-bottom: calc(0.5rem + env(safe-area-inset-bottom, 0px));
}
</style>
